import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-fasting",
  title: "10 Bible verses about fasting, and what each fast was for",
  h1: "Bible verses about fasting",
  description:
    "Ten passages from the World English Bible with a note on each — including the two that criticise fasting, which are the ones most worth reading first.",
  targets: [
    "bible verses about fasting",
    "what does the bible say about fasting",
    "scripture on fasting and prayer",
  ],
  published: "2026-09-03",
  standfirst:
    "Scripture assumes fasting rather than commands it, and the two longest passages about it are both complaints about how it was being done.",
  takeaways: [
    "Jesus says when you fast, not if — it is assumed rather than required, and the instruction attached is about concealing it.",
    "Isaiah 58 is the longest passage on the subject and it is a rejection: the fast God chose is releasing people, feeding them and housing them.",
    "Almost every fast in Scripture has a specific occasion attached — a decision, a danger, a commissioning — rather than being a general discipline.",
    "Matthew 9:15 gives the only reason offered for why the disciples were not fasting: the bridegroom was there. It is about timing, not about rules.",
  ],
  intro: [
    {
      type: "p",
      text: "Fasting is twentieth on the most-searched topic list of the largest topical Bible site, which puts it just inside a list otherwise made of feelings and circumstances. The searches behind it are usually practical — how long, from what, and whether it is required.",
    },
    {
      type: "p",
      text: "The answers are less prescriptive than most people expect. Scripture assumes fasting without commanding it, attaches it to occasions rather than to a calendar, and spends its two longest passages on the subject criticising how it was being done. A modern equivalent — {going without a feed for a stretch} — has the same failure modes and the same fix.",
      links: {
        "going without a feed for a stretch": "/blog/social-media-fast-guide",
      },
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "Moreover when you fast, don't be like the hypocrites, with sad faces. For they disfigure their faces that they may be seen by men to be fasting. Most certainly I tell you, they have received their reward.",
        ref: "Matthew 6:16",
      },
      note: "When rather than if, which is the strongest indication in the gospels that fasting was simply assumed. The correction is entirely about audience, and it sits in a sequence of three — giving, praying, fasting — all with the same structure and the same warning. The reward language is commercial: they have been paid in full, in the currency they chose.",
    },
    {
      verse: {
        text: "Isn't this the fast that I have chosen: to release the bonds of wickedness, to undo the straps of the yoke, to let the oppressed go free, and that you break every yoke?",
        ref: "Isaiah 58:6",
      },
      note: "The longest passage in Scripture about fasting is a rejection of it, and this is its pivot. The people are asking why their fasting has gone unnoticed; the answer is that they fast and quarrel, and exploit their workers on the same day. What is named as the chosen fast is entirely social — releasing, freeing, breaking. It reframes the practice as something done to a situation rather than to yourself.",
    },
    {
      verse: {
        text: 'Yet even now," says Yahweh, "turn to me with all your heart, and with fasting, and with weeping, and with mourning.',
        ref: "Joel 2:12",
      },
      note: "Fasting listed alongside weeping and mourning as one expression of turning, and the next verse is the famous corrective: rend your hearts and not your garments. The order matters — the turning is the thing, and the fasting is one of the forms it takes rather than a mechanism that produces it.",
    },
    {
      verse: {
        text: "When he had fasted forty days and forty nights, he was hungry afterward.",
        ref: "Matthew 4:2",
      },
      note: "The last three words are doing a surprising amount of work. The narrative could have left them out; instead it establishes plainly that the hunger was real, which is what makes the temptation that follows — turn these stones into bread — an actual temptation rather than a formality.",
    },
    {
      verse: {
        text: 'As they served the Lord and fasted, the Holy Spirit said, "Separate Barnabas and Saul for me, for the work to which I have called them."',
        ref: "Acts 13:2",
      },
      note: "One of two places in Acts where fasting accompanies a commissioning, and it is corporate rather than private — a group of named leaders, doing it together, around a decision. Nothing in the passage presents the fast as producing the guidance; it describes the setting the guidance arrived in.",
    },
    {
      verse: {
        text: "Go, gather together all the Jews who are present in Susa, and fast for me, and neither eat nor drink three days, night or day. I and my maidens will also fast the same way. Then I will go in to the king, which is against the law; and if I perish, I perish.",
        ref: "Esther 4:16",
      },
      note: "The most specific fast in Scripture: a fixed duration, a named community, and one clearly stated purpose attached to an action that might get her killed. God is not mentioned anywhere in the book, including here, which makes the fast an unusually bare piece of evidence about what people did when everything depended on something outside their control.",
    },
    {
      verse: {
        text: "Test your servants, I beg you, ten days; and let them give us vegetables to eat and water to drink.",
        ref: "Daniel 1:12",
      },
      note: "Not a fast in the strict sense but a restriction, proposed as an experiment with a time limit and a control group. It is worth including because it is the passage behind most modern partial fasts, and because the reason for it in the narrative is avoiding food connected to the king's table rather than pursuing a spiritual state.",
    },
    {
      verse: {
        text: "Can the friends of the bridegroom mourn as long as the bridegroom is with them? But the days will come when the bridegroom will be taken away from them, and then they will fast.",
        ref: "Matthew 9:15",
      },
      note: "Asked in answer to why his disciples were not fasting when everyone else's were, and the reason given is timing rather than principle. Fasting is treated here as appropriate to absence — which is a genuinely useful frame, because it makes the practice about a gap rather than about earning something.",
    },
    {
      verse: {
        text: "But you, when you fast, anoint your head and wash your face, so that you are not seen by men to be fasting, but by your Father who is in secret; and your Father who sees in secret will reward you.",
        ref: "Matthew 6:17–18",
      },
      note: "The instruction is to look completely normal, which in a culture where fasting was publicly visible was a deliberate act of concealment. Note what is not said: nothing about duration, what to abstain from, or how often. The only regulation offered in the gospels concerns who is watching.",
    },
    {
      verse: {
        text: "For where jealousy and selfish ambition are, there is confusion and every evil deed.",
        ref: "James 3:16",
      },
      note: "Included because it names what Isaiah 58 found underneath the failed fasting — quarrelling and rivalry continuing unaffected while the observance went on. James is describing a community rather than an individual, and the claim is that this kind of disorder does not stay private. A fast that leaves it untouched is the one Isaiah is complaining about.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "what-scripture-does-not-say",
      text: "What Scripture does not say",
    },
    {
      type: "p",
      text: "No duration, no frequency, no required substance, no formula. The gospels' only regulation is about concealment, and the prophets' longest treatment is about whether the rest of your life matches. For a practice with a reputation for rigour, the instructions are remarkably bare.",
    },
    {
      type: "p",
      text: "What is consistent is an occasion. A decision, a danger, a commissioning, a grief. Fasting in Scripture is attached to something, which is a better model than a calendar slot — and it is the model that transfers to {a fast from something other than food}.",
      links: {
        "a fast from something other than food":
          "/blog/social-media-fast-guide",
      },
    },
    {
      type: "note",
      text: "Isaiah 58 is worth reading whole once. It is the passage that decides whether a fast is a practice or a performance, and it is not gentle about the difference.",
    },
  ],
  faq: [
    {
      q: "What does the Bible say about fasting?",
      a: "It assumes it rather than commanding it — Jesus says when you fast, not if — and attaches it to occasions rather than to a schedule. The gospels' only instruction about method concerns concealing it.",
    },
    {
      q: "How long should a fast be?",
      a: "Scripture gives no rule. The fasts recorded run from a single day to three days to forty, and each is attached to a specific occasion rather than to a general discipline.",
    },
    {
      q: "What is the fast God has chosen in Isaiah 58?",
      a: "Releasing people from unjust bonds, feeding the hungry, housing the homeless and clothing the naked. The chapter is a rejection of fasting performed by people who quarrelled and exploited their workers on the same day.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. Joel 2:12 renders the divine name as Yahweh where most translations have 'the LORD'; the New Testament passages here are unaffected.",
    },
  ],
  related: ["social-media-fast-guide", "dopamine-detox-christian"],
  relatedTopics: [
    "bible-verses-about-self-control",
    "bible-verses-about-temptation",
  ],
};
